import React, { useEffect, useState, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import planApi from '@api/planApi';
import { AuthContext } from '@context';
import { 
  Building2, PersonStanding, FolderCheck, AlertTriangle, 
  Calendar, ChevronRight, Clock, FileX, TrendingUp, ArrowUpRight, Circle 
} from 'lucide-react';
import { clsx } from "clsx";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

// 방문 D-Day 계산 함수
function getDDay(dateStr) {
  if (!dateStr) return "-";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(dateStr);
  targetDate.setHours(0, 0, 0, 0);
  
  const diff = Math.round((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diff === 0) return "D-Day";
  if (diff > 0)  return `D-${diff}`;
  return `D+${Math.abs(diff)}`;
}

// 인사말 함수
function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "좋은 아침입니다";
  if (h < 18) return "안녕하세요";
  return "수고 많으셨습니다";
}

// 커스텀 툴팁 (Bar Chart)
function CustomBarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg text-xs">
      <p className="font-semibold text-slate-700 mb-1.5">{label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 mb-1 last:mb-0">
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: p.fill }} />
          <span className="text-slate-500">{p.name}</span>
          <span className="font-semibold text-slate-800 ml-auto pl-3">{p.value}건</span>
        </div>
      ))}
    </div>
  );
}

// 커스텀 툴팁 (Donut)
function CustomDonutTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;
  const pct = total > 0 ? Math.round((payload[0].value / total) * 100) : 0;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg text-xs z-50 relative">
      <p className="font-semibold text-slate-700">{payload[0].name}</p>
      <p className="text-slate-500 mt-0.5">{payload[0].value}건 ({pct}%)</p>
    </div>
  );
}

export function Dashboard() {
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [greeting, setGreeting] = useState('');
  const [todayStr, setTodayStr] = useState('');

  useEffect(() => {
    setGreeting(getGreeting());
    const options = { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' };
    setTodayStr(new Intl.DateTimeFormat('ko-KR', options).format(new Date()));
  }, []);

  const { data: response, isLoading } = useQuery({
    queryKey: ['plan', 'dashboard'],
    queryFn: () => planApi.getDashboardSummary()
  });

  const rawData = response?.data || response; 
  const data = rawData?.kpi ? rawData : {
    kpi: { totalAssigned: 0, completedVisits: 0, uploadedFiles: 0, delayedCount: 0 },
    monthlyStats: [],
    upcomingVisits: [],
    actionRequired: []
  };

  // 파생 변수 계산
  const visitCompletionRate = data.kpi.totalAssigned > 0 
    ? Math.round((data.kpi.completedVisits / data.kpi.totalAssigned) * 100) : 0;
  const deliverableUploadRate = data.kpi.completedVisits > 0 
    ? Math.round((data.kpi.uploadedFiles / data.kpi.completedVisits) * 100) : 0;

  // 파이 차트 데이터
  const donutData = [
    { name: "방문 예정", value: data.kpi.totalAssigned - data.kpi.completedVisits, color: "#94a3b8" },
    { name: "완료·서류 미등록", value: data.kpi.completedVisits - data.kpi.uploadedFiles, color: "#f59e0b" },
    { name: "최종 완료", value: data.kpi.uploadedFiles, color: "#22c55e" },
  ].filter(d => d.value > 0);

  if (isLoading) return <div className="flex h-64 items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div></div>;

  const isAdmin = auth?.role === 'ROLE_ADMIN';

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-500 pb-10">

      {/* SECTION 1 — 환영 메시지 + KPI 카드 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
        <div>
          <p className="text-sm text-slate-400">{todayStr}</p>
          <h2 className="text-2xl font-bold text-slate-800">
            {greeting}, {auth?.name}님 👋
          </h2>
        </div>
        
        {data.kpi.delayedCount > 0 && (
          <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 mt-2 sm:mt-0 shadow-sm">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
            <span className="text-sm text-amber-700">
              처리가 필요한 알림이 <strong>{data.kpi.delayedCount}건</strong> 있습니다.
            </span>
            <button
              onClick={() => navigate("/progress")}
              className="ml-1 text-xs font-semibold text-amber-600 underline underline-offset-2 hover:text-amber-800 transition-colors"
            >
              바로가기
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {/* 1. 전체 배정 사업장 */}
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <span className="flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600">
              <TrendingUp className="h-3 w-3" /> {new Date().getFullYear()}년
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-1">전체 배정 사업장</p>
          <p className="text-3xl font-bold text-blue-600">{data.kpi.totalAssigned.toLocaleString()}<span className="text-base font-medium text-slate-400 ml-1">개소</span></p>
          <p className="text-xs text-slate-400 mt-1.5">총 방문 배정 회차 기준</p>
          <div className="absolute -right-4 -bottom-4 h-24 w-24 rounded-full bg-blue-50/60 blur-xl" />
        </div>

        {/* 2. 컨설팅 실시율 */}
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50">
              <PersonStanding className="h-6 w-6 text-indigo-600" />
            </div>
            <span className={clsx(
              "flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold",
              visitCompletionRate >= 70 ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"
            )}>
              <ArrowUpRight className="h-3 w-3" />{visitCompletionRate >= 70 ? "양호" : "주의"}
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-1">컨설팅 실시율</p>
          <p className="text-3xl font-bold text-indigo-600">{visitCompletionRate}<span className="text-base font-medium text-slate-400 ml-0.5">%</span></p>
          <div className="mt-3 space-y-1.5">
            <div className="flex justify-between text-xs text-slate-400">
              <span>{data.kpi.completedVisits}건 완료</span>
              <span>{data.kpi.totalAssigned}건 배정</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-indigo-500 transition-all duration-1000" style={{ width: `${visitCompletionRate}%` }} />
            </div>
          </div>
        </div>

        {/* 3. 산출물 등록률 */}
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50">
              <FolderCheck className="h-6 w-6 text-teal-600" />
            </div>
            <span className={clsx(
              "flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold",
              deliverableUploadRate >= 80 ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"
            )}>
              <ArrowUpRight className="h-3 w-3" />{deliverableUploadRate >= 80 ? "양호" : "주의"}
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-1">산출물 등록률</p>
          <p className="text-3xl font-bold text-teal-600">{deliverableUploadRate}<span className="text-base font-medium text-slate-400 ml-0.5">%</span></p>
          <div className="mt-3 space-y-1.5">
            <div className="flex justify-between text-xs text-slate-400">
              <span>{data.kpi.uploadedFiles}건 등록</span>
              <span>{data.kpi.completedVisits}건 방문 완료</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-teal-500 transition-all duration-1000" style={{ width: `${deliverableUploadRate}%` }} />
            </div>
          </div>
        </div>

        {/* 4. 요주의 / 지연 알림 */}
        <button
          onClick={() => navigate("/progress")}
          className="relative overflow-hidden rounded-2xl border border-red-200 bg-red-50 p-5 shadow-sm hover:shadow-md hover:border-red-300 hover:bg-red-100/60 transition-all text-left group"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
            <span className="flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-600 group-hover:bg-red-200 transition-colors">
              조치 필요 <ChevronRight className="h-3 w-3" />
            </span>
          </div>
          <p className="text-sm text-red-500 mb-1">요주의 / 지연 건수</p>
          <p className="text-3xl font-bold text-red-600">{data.kpi.delayedCount}<span className="text-base font-medium text-red-400 ml-1">건</span></p>
          <p className="text-xs text-red-400 mt-1.5">미방문 + 서류 미등록 합산</p>
          <div className="absolute -right-4 -bottom-4 h-24 w-24 rounded-full bg-red-100/80 blur-xl" />
        </button>
      </div>

      {/* SECTION 2 — 시각화 차트 */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-5">
            <div>
              <h3 className="font-bold text-slate-800">월별 컨설팅 진행 추이</h3>
              <p className="text-xs text-slate-400 mt-0.5">방문 예정 vs 방문 완료 ({new Date().getFullYear()}년)</p>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm bg-slate-300 shrink-0" /> 방문 예정
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm bg-blue-500 shrink-0" /> 방문 완료
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.monthlyStats} barCategoryGap="30%" barGap={3}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} dy={10} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} width={28} />
              <Tooltip content={<CustomBarTooltip />} cursor={{ fill: "#f8fafc" }} isAnimationActive={false} />
              <Bar dataKey="planned" name="배정 건수" fill="#cbd5e1" radius={[4, 4, 0, 0]} maxBarSize={40} />
              <Bar dataKey="completed" name="실시 완료" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex flex-col">
          <div className="mb-4">
            <h3 className="font-bold text-slate-800">전체 진행 현황</h3>
            <p className="text-xs text-slate-400 mt-0.5">파이프라인 단계별 비율</p>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center">
            {donutData.length > 0 ? (
              <>
                <div className="relative">
                  <PieChart width={200} height={180}>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius={58} outerRadius={82} paddingAngle={3} dataKey="value" startAngle={90} endAngle={-270} isAnimationActive={false}>
                      {donutData.map((entry, idx) => <Cell key={`donut-cell-${idx}`} fill={entry.color} stroke="none" />)}
                    </Pie>
                    <Tooltip content={<CustomDonutTooltip total={data.kpi.totalAssigned} />} isAnimationActive={false} />
                  </PieChart>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-2xl font-bold text-slate-800">{data.kpi.totalAssigned}</p>
                    <p className="text-xs text-slate-400">전체</p>
                  </div>
                </div>
                <div className="w-full mt-2 space-y-2.5">
                  {donutData.map((d) => {
                    const pct = Math.round((d.value / data.kpi.totalAssigned) * 100);
                    return (
                      <div key={d.name} className="flex items-center gap-2.5">
                        <Circle className="h-3 w-3 shrink-0" style={{ color: d.color, fill: d.color }} />
                        <span className="flex-1 text-xs text-slate-500 truncate">{d.name}</span>
                        <span className="text-xs font-semibold text-slate-700">{d.value}건</span>
                        <span className="text-xs text-slate-400 w-8 text-right">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-sm text-slate-400">데이터가 없습니다.</div>
            )}
          </div>
        </div>
      </div>

      {/* SECTION 3 — To-Do & 알림 테이블 */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* 좌측: 다가오는 방문 일정 */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                <Calendar className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-sm">다가오는 방문 일정</h3>
                <p className="text-xs text-slate-400">오늘부터 7일 이내 · Top 5</p>
              </div>
            </div>
            <button onClick={() => navigate("/visit-management")} className="flex items-center gap-1 rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors">
              전체 보기 <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70">
                <tr>
                  {["사업장명", "회차", "방문 예정일", isAdmin && "담당자"].filter(Boolean).map((h, i) => (
                    <th key={i} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.upcomingVisits.length > 0 ? data.upcomingVisits.map((v) => {
                  const dday = getDDay(v.visitDate);
                  const isToday = dday === "D-Day";
                  const isUrgent = dday === "D-1" || isToday;
                  return (
                    <tr key={v.assignNo} 
                        onClick={() => navigate("/visit-management", { state: { searchBizName: v.bizName } })}
                        className="group cursor-pointer hover:bg-blue-50/40 transition-colors">
                      <td className="px-4 py-3 font-medium text-slate-700 group-hover:text-blue-700 whitespace-nowrap">{v.bizName}</td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium">{v.visitRound}회차</span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-slate-300" />
                          {v.visitDate}
                        </div>
                      </td>
                      {isAdmin && <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{v.staffNm}</td>}
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <span className={clsx("rounded-full px-2.5 py-1 text-xs font-bold", isToday ? "bg-red-100 text-red-600" : isUrgent ? "bg-amber-100 text-amber-600" : "bg-blue-50 text-blue-600")}>
                          {dday}
                        </span>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={isAdmin ? 5 : 4} className="p-8 text-center text-sm text-slate-500">다가오는 일정이 없습니다.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 우측: 조치 필요 항목 */}
        <div className="rounded-2xl border border-red-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-red-100 bg-red-50/40">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100">
                <FileX className="h-4 w-4 text-red-500" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-sm">조치 필요 알림</h3>
                <p className="text-xs text-slate-400">지연 및 누락 항목 · Top 5</p>
              </div>
            </div>
            <button onClick={() => navigate("/progress")} className="flex items-center gap-1 rounded-lg bg-white border border-red-200 px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors">
              진행현황 가기 <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70">
                <tr>
                  {["사업장명", "회차", "지연 상태", "지연 일수", ""].map((h, i) => (
                    <th key={i} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.actionRequired.length > 0 ? data.actionRequired.map((a) => (
                  <tr key={a.assignNo} onClick={() => {
                      const routeState = { state: { searchBizName: a.bizName } };
                      if (a.issue === '방문 지연' || a.issue === '방문 미실시') navigate('/visit-management',routeState);
                      else if (a.issue === '산출물 미등록' || a.issue === '서류 미등록') navigate('/deliverables',routeState);
                      else navigate('/progress');
                    }} className="group cursor-pointer hover:bg-red-50/40 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-700 group-hover:text-red-700 whitespace-nowrap">{a.bizName}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{a.visitRound}회차</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={clsx("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold", a.issue.includes("방문") ? "bg-orange-100 text-orange-600" : "bg-yellow-100 text-yellow-700")}>
                        {a.issue.includes("방문") ? <AlertTriangle className="h-3 w-3" /> : <FileX className="h-3 w-3" />}
                        {a.issue}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={clsx("rounded-full px-2.5 py-1 text-xs font-bold", a.daysDelayed >= 10 ? "bg-red-100 text-red-600" : a.daysDelayed >= 5 ? "bg-orange-100 text-orange-600" : "bg-amber-100 text-amber-600")}>
                        D+{a.daysDelayed}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-red-400 transition-colors ml-auto" />
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={5} className="p-8 text-center text-sm text-slate-500">지연되거나 누락된 항목이 없습니다. 🎉</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}